import { Client, createClient } from "graphql-ws";
import {
  Environment,
  FetchFunction,
  GraphQLResponse,
  Network,
  Observable,
  RecordSource,
  RequestParameters,
  Store,
  SubscribeFunction,
  UploadableMap,
  Variables,
} from "relay-runtime";
import { assertNever, isEmptyObject, sleep } from "@/utils/helpers";
import { AppHandle } from "./state";
import { relayFieldLogger } from "../relayEnvironment";

export function createRelayEnvironment(
  app: AppHandle,
  userId: string | undefined,
  shouldHydrate: boolean,
): [Environment, () => void] {
  const ws = createClient({
    url: wsEndpoint.toString(),
    // A dropped socket reports a plain `error` Event before its CloseEvent,
    // which graphql-ws treats as fatal by default.
    shouldRetry: () => true,
    retryAttempts: 10,
    retryWait: (retries) =>
      sleep(Math.min(1_000 * 2 ** retries, 30_000) + Math.random() * 1_000),
  });
  const network = Network.create(relayFetch(app), relaySubscribe(ws));
  const store = new Store(
    new RecordSource(shouldHydrate ? extractDocumentAppData() : undefined),
  );
  const environment = new Environment({
    network,
    store,
    options: { userId },
    relayFieldLogger,
  });
  return [environment, ws.dispose];
}

const parseEndpoint = (endpointStr: string, context: Document = document) => {
  const endpoint = new URL(endpointStr, context.baseURI);
  const wsEndpoint = new URL(endpoint);
  wsEndpoint.protocol = endpoint.protocol === "https:" ? "wss:" : "ws:";
  return [endpoint, wsEndpoint];
};

const [endpoint, wsEndpoint] = parseEndpoint(
  import.meta.env.VITE_API_URL || "/graphql",
);

function extractDocumentAppData() {
  return window.__AQORA_RELAY__;
}

const relayFetch =
  (app: AppHandle): FetchFunction =>
  (request, variables, _cacheConfig, uploadables) =>
    Observable.create((sink) => {
      const abort = new AbortController();

      const promise =
        !uploadables || isEmptyObject(uploadables)
          ? sendRelayRequest(app, request, variables, abort.signal)
          : sendRelayMultipartRequest(
              app,
              request,
              variables,
              uploadables,
              abort.signal,
            );

      promise.then(
        ([response, action]) => {
          sink.next(response);
          sink.complete();
          if (action) {
            applyAction(app, action);
          }
        },
        (error) => {
          sink.error(error);
        },
      );

      return () => abort.abort();
    });

function getRequestHeaders(app: AppHandle) {
  const headers = new Headers();
  const token = app.token;
  if (token) {
    headers.set("authorization", `Bearer ${token.token}`);
  }
  return headers;
}

async function sendRelayRequest(
  app: AppHandle,
  request: RequestParameters,
  variables: Variables,
  signal: AbortSignal,
) {
  const headers = getRequestHeaders(app);
  headers.set("content-type", "application/json");

  const body = JSON.stringify({
    query: request.text,
    variables,
  });

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    body,
    signal,
  });

  return receiveRelayResponse(response);
}

async function sendRelayMultipartRequest(
  app: AppHandle,
  request: RequestParameters,
  variables: Variables,
  uploadables: UploadableMap,
  signal: AbortSignal,
) {
  const headers = getRequestHeaders(app);

  const body = new FormData();
  body.append("operations", JSON.stringify({ query: request.text, variables }));

  const map: Record<number, string[]> = {};
  Object.keys(uploadables).forEach((key, index) => {
    map[index] = [key];
  });
  body.append("map", JSON.stringify(map));

  for (const [fileKey, variables] of Object.entries(map)) {
    for (const variable of variables) {
      body.append(fileKey, uploadables[variable]);
    }
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    body,
    signal,
  });

  return receiveRelayResponse(response);
}

async function receiveRelayResponse(
  response: Response,
): Promise<[GraphQLResponse, Action | undefined]> {
  const access = response.headers.get("x-access-token");
  const refresh = response.headers.get("x-refresh-token");
  const responseData = await response.json();

  let action: Action | undefined;
  if (response.headers.has("x-revoke-tokens")) {
    action = { type: "revoke-all" };
  } else if (response.headers.has("x-refresh-tokens")) {
    action = { type: "revoke-access" };
  } else if (access !== null || refresh !== null) {
    action = { type: "set-tokens", access, refresh };
  } else if (isInvalidAuthorizationResponse(responseData)) {
    action = { type: "revoke-all" };
  }

  return [responseData, action];
}

function isInvalidAuthorizationResponse(json: unknown): boolean {
  if (
    typeof json === "object" &&
    json !== null &&
    "errors" in json &&
    Array.isArray(json["errors"])
  ) {
    for (const error of json["errors"]) {
      if (
        typeof error === "object" &&
        typeof error["extensions"] === "object" &&
        typeof error["extensions"]["code"] === "string" &&
        error["extensions"]["code"] === "INVALID_AUTHORIZATION"
      ) {
        return true;
      }
    }
  }
  return false;
}

type Action =
  | { type: "revoke-all" }
  | { type: "revoke-access" }
  | { type: "set-tokens"; access: string | null; refresh: string | null };

function applyAction(app: AppHandle, action: Action) {
  switch (action.type) {
    case "revoke-all":
      return app.revokeAll();
    case "revoke-access":
      return app.revokeAccess();
    case "set-tokens":
      return app.setTokens(action.access, action.refresh);
    default:
      assertNever(action);
  }
}

const relaySubscribe =
  (client: Client): SubscribeFunction =>
  (request, variables, _cacheConfig) =>
    Observable.create((sink) => {
      if (request.text === null) {
        try {
          sink.error(new Error("Operation text cannot be empty"));
        } finally {
          sink.complete();
        }
      } else {
        return client.subscribe(
          { operationName: request.name, query: request.text, variables },
          {
            next: (response) => sink.next(response as GraphQLResponse),
            error: (error) => sink.error(toSubscriptionError(error)),
            complete: () => sink.complete(),
          },
        );
      }
    });

function toSubscriptionError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  if (Array.isArray(error)) {
    return new Error(error.map((e) => e?.message ?? String(e)).join("\n"));
  }
  if (typeof error === "object" && error !== null && "code" in error) {
    const { code, reason } = error as CloseEvent;
    return new Error(
      `Subscription connection closed (${code}${reason ? `: ${reason}` : ""})`,
    );
  }
  return new Error("Subscription connection failed");
}
